import { useEffect, useRef, useState } from 'react';
import Lottie from 'lottie-react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  EyeIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullseye } from '@fortawesome/free-solid-svg-icons';

gsap.registerPlugin(ScrollTrigger);

const LOTTIE_URLS = {
  vision:   'https://assets3.lottiefiles.com/packages/lf20_jcikwtux.json',
  mission:  'https://assets9.lottiefiles.com/packages/lf20_bd3k9p9j.json',
  story:    'https://assets10.lottiefiles.com/packages/lf20_fcfjwiyb.json',
};

const STATS = [
  { label: 'Happy clients',     value: 12000, suffix: '+', float: false },
  { label: 'Expert providers',  value: 500,   suffix: '+', float: false },
  { label: 'Average rating',    value: 4.8,   suffix: '★', float: true  },
  { label: 'Satisfaction rate', value: 98,    suffix: '%', float: false },
];

/* ─────────────────────────────────────────────────────────────────────────────
   LottieIcon — fetches JSON from URL and renders it; Heroicon fallback while
   loading or on failure so the layout never breaks.
   ─────────────────────────────────────────────────────────────────────────── */
function LottieIcon({ url, FallbackIcon, size = 60 }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!url) return;
    let dead = false;
    fetch(url)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(json => { if (!dead) setData(json); })
      .catch(() => {});
    return () => { dead = true; };
  }, [url]);

  if (data) {
    return <Lottie animationData={data} loop autoplay style={{ width: size, height: size }} />;
  }

  return (
    <div
      style={{
        width: size, height: size,
        borderRadius: Math.round(size * 0.28),
        background: 'var(--orange-3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <FallbackIcon style={{ width: size * 0.48, height: size * 0.48, color: 'var(--orange-9)' }} />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   SplitSection — reusable two-column layout used for Vision / Mission / Story.
   ─────────────────────────────────────────────────────────────────────────── */
function SplitSection({
  eyebrow, title, body, cta,
  imgUrl, imgAlt,
  dotLottieSrc,
  lottieUrl, FallbackIcon,
  customIcon,
  imgOnRight = false,
  tinted = false,
}) {
  const accentPos = imgOnRight ? { right: -16, top: -16 } : { left: -16, top: -16 };

  const textCol = (
    <div style={{ flex: '1 1 340px', minWidth: 0 }}>
      <div className="au-item" style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
        {customIcon ?? <LottieIcon url={lottieUrl} FallbackIcon={FallbackIcon} size={52} />}
        <span style={{
          fontSize: 'var(--font-size-small)', fontWeight: 700,
          letterSpacing: '0.08em', textTransform: 'uppercase',
          color: 'var(--orange-9)',
        }}>
          {eyebrow}
        </span>
      </div>

      <h2 className="au-item" style={{
        fontSize: 'var(--font-size-page-title)', fontWeight: 700,
        color: 'var(--gray-12)', lineHeight: 1.18,
        letterSpacing: '-0.015em', marginBottom: 18,
      }}>
        {title}
      </h2>

      <p className="au-item" style={{
        fontSize: 'var(--font-size-lead)', color: 'var(--gray-11)',
        lineHeight: 1.78, marginBottom: 30,
      }}>
        {body}
      </p>

      <button
        className="au-item"
        style={{
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 8,
          fontSize: 'var(--font-size-body)', fontWeight: 600,
          color: 'var(--orange-9)', fontFamily: 'Poppins, sans-serif',
          transition: 'gap 0.2s ease',
        }}
        onMouseEnter={e => { e.currentTarget.style.gap = '14px'; }}
        onMouseLeave={e => { e.currentTarget.style.gap = '8px'; }}
      >
        {cta}
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  );

  const imgCol = (
    <div className="img-side" style={{ flex: '1 1 360px', minWidth: 0, position: 'relative' }}>
      <div style={{
        position: 'absolute', ...accentPos,
        width: '52%', height: '52%',
        background: 'var(--orange-3)',
        borderRadius: 24, zIndex: 0,
      }} />
      {dotLottieSrc ? (
        <DotLottieReact
          src={dotLottieSrc}
          loop
          autoplay
          style={{ width: '100%', aspectRatio: '4/3', position: 'relative', zIndex: 1 }}
        />
      ) : (
        <img
          src={imgUrl}
          alt={imgAlt}
          loading="lazy"
          style={{
            width: '100%', aspectRatio: '4/3',
            objectFit: 'cover', borderRadius: 24,
            position: 'relative', zIndex: 1, display: 'block',
            boxShadow: '0 24px 64px rgba(0,0,0,0.12)',
          }}
        />
      )}
    </div>
  );

  return (
    <section
      className="split-section"
      style={{
        padding: 'clamp(64px, 10vw, 120px) clamp(24px, 6vw, 80px)',
        background: tinted ? 'var(--gray-1)' : 'var(--color-background)',
      }}
    >
      <div style={{
        maxWidth: 1100, margin: '0 auto',
        display: 'flex', flexWrap: 'wrap',
        alignItems: 'center',
        gap: 'clamp(40px, 6vw, 80px)',
      }}>
        {imgOnRight ? <>{textCol}{imgCol}</> : <>{imgCol}{textCol}</>}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Values section — animated SVG card art, no external assets
   ─────────────────────────────────────────────────────────────────────────── */
const VALUES_CSS = `
.au-values-grid{ --au-coral:#f05430; }

.au-value-card{
  border-radius:22px; border:1px solid var(--gray-4);
  background:#ffffff;
  display:flex; flex-direction:column;
  overflow:hidden; cursor:default;
  transition:box-shadow .24s ease, transform .24s ease, border-color .24s ease;
}
.au-value-card:hover{
  box-shadow:0 10px 38px rgba(240,84,48,.18);
  transform:translateY(-5px);
  border-color:rgba(240,84,48,.35);
}
.au-value-card .au-art{
  width:100%; height:260px; flex-shrink:0;
  overflow:hidden;
  transition:transform .5s cubic-bezier(.2,.7,.2,1);
}
.au-value-card:hover .au-art{ transform:scale(1.05); }
.au-svg{ width:100%; height:100%; display:block; }

.au-scrim{ display:none; }

.au-content{ padding:clamp(18px,2.5vw,26px) clamp(20px,2.5vw,28px); border-top:1px solid var(--gray-3,#f0eeec); }
.au-content h3{
  font-size:var(--font-size-card-title); font-weight:600;
  color:var(--gray-12); margin:0 0 8px; letter-spacing:-.01em;
}
.au-content h3::before{
  content:""; display:block; width:30px; height:3px; border-radius:3px;
  background:var(--au-coral); margin-bottom:12px; transition:width .26s ease;
}
.au-value-card:hover .au-content h3::before{ width:48px; }
.au-content p{
  font-size:var(--font-size-body); line-height:1.7;
  color:var(--gray-11); margin:0;
}

@keyframes auGlow{0%,100%{opacity:.65;transform:scale(.96)}50%{opacity:1;transform:scale(1.05)}}
@keyframes auFloatUp{0%{transform:translateY(0);opacity:0}18%{opacity:.95}100%{transform:translateY(-120px);opacity:0}}
@keyframes auPulse{0%{transform:scale(.5);opacity:.85}100%{transform:scale(2.4);opacity:0}}
@keyframes auTwinkle{0%,100%{opacity:.15;transform:scale(.6)}50%{opacity:1;transform:scale(1.12)}}
@keyframes auDash{to{stroke-dashoffset:0}}
@keyframes auShine{0%{transform:translateX(-160px)}55%,100%{transform:translateX(180px)}}
@keyframes auSnip{0%,100%{transform:rotate(-9deg)}50%{transform:rotate(6deg)}}
@keyframes auFlicker{0%,100%{transform:scale(1,1);opacity:.95}25%{transform:scale(1.05,.94);opacity:1}50%{transform:scale(.95,1.07);opacity:1}75%{transform:scale(1.03,.97);opacity:.92}}

[class^="au-a-"]{transform-box:fill-box;transform-origin:center}
.au-a-glow{animation:auGlow 5.5s ease-in-out infinite}
.au-a-float{animation:auFloatUp 5s linear infinite}
.au-a-pulse{animation:auPulse 3s ease-out infinite}
.au-a-twinkle{animation:auTwinkle 2.8s ease-in-out infinite}
.au-a-shine{animation:auShine 4.5s ease-in-out infinite}
.au-a-snip{animation:auSnip 2.4s ease-in-out infinite}
.au-a-snip-2{animation:auSnip 2.4s ease-in-out infinite reverse}
.au-a-flicker{animation:auFlicker 2.2s ease-in-out infinite}

@media (prefers-reduced-motion: reduce){
  [class^="au-a-"]{animation:none !important}
  .au-value-card:hover .au-art{transform:none}
}
`;

const Ground = ({ id }) => (
  <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
    <stop offset="0%" stopColor="#fff8f5" />
    <stop offset="55%" stopColor="#fdeee7" />
    <stop offset="100%" stopColor="#fce3d8" />
  </linearGradient>
);

const ArtBooking = () => {
  const cells = [];
  [178, 222, 266].forEach(y =>
    [140, 188, 236].forEach(x => cells.push({ x, y, booked: x === 188 && y === 222 }))
  );
  return (
    <svg className="au-svg" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
      <defs><Ground id="g-bk" /></defs>
      <rect width="400" height="400" fill="url(#g-bk)" />
      <rect x="150" y="94" width="10" height="20" rx="4" fill="#fbefe7" />
      <rect x="240" y="94" width="10" height="20" rx="4" fill="#fbefe7" />
      <rect x="120" y="104" width="160" height="196" rx="18" fill="#fbefe7" />
      <path d="M120 148 V122 a18 18 0 0 1 18 -18 H262 a18 18 0 0 1 18 18 V148 Z" fill="#f05430" />
      {cells.map(({ x, y, booked }, i) => (
        <rect key={i} x={x - 15} y={y - 12} width="30" height="24" rx="6" fill={booked ? "#f05430" : "#f3dccf"} />
      ))}
      <circle className="au-a-pulse" cx="188" cy="222" r="20" fill="none" stroke="#f0846c" strokeWidth="3"
        style={{ transformOrigin: "188px 222px" }} />
      <path d="M178 222 l7 7 l14 -16" fill="none" stroke="#f05430" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"
        style={{ strokeDasharray: 50, strokeDashoffset: 50, animation: "auDash 2.4s ease-in-out infinite alternate" }} />
      <path className="au-a-glow" d="M270 92 l-18 30 h11 l-9 26 24 -34 h-13 Z" fill="#f0846c"
        style={{ transformOrigin: "262px 118px" }} />
    </svg>
  );
};

const ArtScissors = () => (
  <svg className="au-svg" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
    <defs><Ground id="g-sc" /></defs>
    <rect width="400" height="400" fill="url(#g-sc)" />
    <g className="au-a-snip" style={{ transformBox: "view-box", transformOrigin: "200px 212px" }}>
      <path d="M200 212 L150 96 L162 92 L210 206 Z" fill="#efe9e0" />
      <path d="M200 212 C 222 250 244 270 250 286" fill="none" stroke="#f05430" strokeWidth="9" strokeLinecap="round" />
      <circle cx="256" cy="300" r="18" fill="none" stroke="#f05430" strokeWidth="9" />
    </g>
    <g className="au-a-snip-2" style={{ transformBox: "view-box", transformOrigin: "200px 212px" }}>
      <path d="M200 212 L250 96 L238 92 L190 206 Z" fill="#d8d2c8" />
      <path d="M200 212 C 178 250 156 270 150 286" fill="none" stroke="#e46c48" strokeWidth="9" strokeLinecap="round" />
      <circle cx="144" cy="300" r="18" fill="none" stroke="#e46c48" strokeWidth="9" />
    </g>
    <circle cx="200" cy="212" r="6" fill="#f05430" />
    <path className="au-a-twinkle" d="M150 96 m0 -10 l4 8 l8 2 l-8 4 l-4 8 l-4 -8 l-8 -4 l8 -2 Z" fill="#fff3ea"
      style={{ transformOrigin: "150px 96px" }} />
  </svg>
);

const heart = (cx, cy, s) =>
  `M${cx} ${cy + s * 0.32} C ${cx} ${cy} ${cx - s} ${cy} ${cx - s} ${cy - s * 0.5} C ${cx - s} ${cy - s} ${cx} ${cy - s} ${cx} ${cy - s * 0.4} C ${cx} ${cy - s} ${cx + s} ${cy - s} ${cx + s} ${cy - s * 0.5} C ${cx + s} ${cy} ${cx} ${cy} ${cx} ${cy + s * 0.32} Z`;

const ArtHearts = () => {
  const floats = [[120, 18, 0], [168, 13, 0.9], [232, 16, 1.8], [280, 12, 2.6], [150, 11, 3.4], [252, 10, 4.2]];
  return (
    <svg className="au-svg" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <Ground id="g-ht" />
        <linearGradient id="ht-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f0846c" /><stop offset="100%" stopColor="#f05430" />
        </linearGradient>
      </defs>
      <rect width="400" height="400" fill="url(#g-ht)" />
      {floats.map(([x, s, d], i) => (
        <path key={i} className="au-a-float" d={heart(x, 320, s)} fill="#f0846c" opacity="0"
          style={{ animationDelay: `${d}s` }} />
      ))}
      <path className="au-a-glow" d={heart(200, 205, 60)} fill="url(#ht-fill)" style={{ transformOrigin: "200px 195px" }} />
    </svg>
  );
};

const ArtSparkle = () => {
  const star = (cx, cy, s) =>
    `M${cx} ${cy - s} Q${cx + s * 0.18} ${cy - s * 0.18} ${cx + s} ${cy} Q${cx + s * 0.18} ${cy + s * 0.18} ${cx} ${cy + s} Q${cx - s * 0.18} ${cy + s * 0.18} ${cx - s} ${cy} Q${cx - s * 0.18} ${cy - s * 0.18} ${cx} ${cy - s} Z`;
  const pts = [[110, 120, 32], [300, 102, 22], [330, 250, 18], [92, 282, 20], [212, 322, 16], [252, 178, 13]];
  return (
    <svg className="au-svg" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <Ground id="g-sp" />
        <radialGradient id="sp-hero"><stop offset="0%" stopColor="#fff3ea" /><stop offset="100%" stopColor="#f0846c" /></radialGradient>
      </defs>
      <rect width="400" height="400" fill="url(#g-sp)" />
      <path className="au-a-glow" d={star(200, 200, 66)} fill="url(#sp-hero)" style={{ transformOrigin: "200px 200px" }} />
      {pts.map(([x, y, s], i) => (
        <path key={i} className="au-a-twinkle" d={star(x, y, s)} fill={i % 2 ? "#f0846c" : "#fff3ea"}
          style={{ transformOrigin: `${x}px ${y}px`, animationDelay: `${i * 0.5}s` }} />
      ))}
    </svg>
  );
};

const ArtCandle = () => (
  <svg className="au-svg" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <Ground id="g-cd" />
      <radialGradient id="cd-glow"><stop offset="0%" stopColor="#f0846c" stopOpacity=".85" /><stop offset="100%" stopColor="#f05430" stopOpacity="0" /></radialGradient>
      <linearGradient id="cd-flame" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#ffd9b0" /><stop offset="100%" stopColor="#f05430" />
      </linearGradient>
    </defs>
    <rect width="400" height="400" fill="url(#g-cd)" />
    <circle className="au-a-glow" cx="200" cy="172" r="86" fill="url(#cd-glow)" style={{ transformOrigin: "200px 172px" }} />
    {[[196, 0], [205, 1.4], [200, 2.6]].map(([x, d], i) => (
      <circle key={i} className="au-a-float" cx={x} cy="150" r="5" fill="#fff" opacity="0" style={{ animationDelay: `${d}s` }} />
    ))}
    <g className="au-a-flicker" style={{ transformBox: "view-box", transformOrigin: "200px 196px" }}>
      <path d="M200 150 C 214 172 212 196 200 196 C 188 196 186 172 200 150 Z" fill="url(#cd-flame)" />
      <path d="M200 166 C 207 178 206 192 200 192 C 194 192 193 178 200 166 Z" fill="#ffc9a0" />
    </g>
    <line x1="200" y1="196" x2="200" y2="206" stroke="#3a241a" strokeWidth="3" />
    <ellipse cx="200" cy="206" rx="30" ry="7" fill="#fff4ea" />
    <rect x="170" y="206" width="60" height="152" rx="12" fill="#fbefe7" />
    <rect x="170" y="206" width="18" height="152" rx="12" fill="#ffffff" opacity=".35" />
  </svg>
);

const ArtShield = () => (
  <svg className="au-svg" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <Ground id="g-sh" />
      <linearGradient id="sh-grad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#f0846c" /><stop offset="100%" stopColor="#f05430" />
      </linearGradient>
      <clipPath id="sh-clip"><path d="M200 96l78 26v74c0 56-38 88-78 104c-40-16-78-48-78-104v-74Z" /></clipPath>
    </defs>
    <rect width="400" height="400" fill="url(#g-sh)" />
    <circle className="au-a-pulse" cx="200" cy="210" r="70" fill="none" stroke="#f0846c" strokeWidth="3"
      style={{ transformOrigin: "200px 210px" }} />
    <path d="M200 96l78 26v74c0 56-38 88-78 104c-40-16-78-48-78-104v-74Z" fill="url(#sh-grad)" />
    <g clipPath="url(#sh-clip)">
      <rect className="au-a-shine" x="80" y="80" width="40" height="280" fill="#fff" opacity=".4" transform="skewX(-18)" />
    </g>
    <path d="M168 206l24 24l44-50" fill="none" stroke="#181818" strokeWidth="11" strokeLinecap="round" strokeLinejoin="round"
      style={{ strokeDasharray: 120, strokeDashoffset: 120, animation: "auDash 3s ease-in-out infinite alternate" }} />
  </svg>
);

const VALUES = [
  { Art: ArtBooking,  title: 'Fast, reliable booking',  body: 'Pick a time, tap once, done. Real-time availability, instant confirmation, and reminders so you never miss your slot.' },
  { Art: ArtScissors, title: 'Expert stylists',          body: 'Skilled, friendly pros who listen first — then cut, color, and style to suit you and your hair.' },
  { Art: ArtCandle,   title: 'Sit back and relax',       body: 'More than a haircut — a moment that\'s yours. Settle in, switch off, and let us handle the rest.' },
  { Art: ArtShield,   title: 'Clean and safe',           body: 'Freshly sanitized stations and tools for every guest. Your comfort and wellbeing always come first.' },
];

function ValuesSection() {
  return (
    <>
      <style>{VALUES_CSS}</style>
      <section style={{ padding: 'clamp(64px,10vw,120px) clamp(24px,6vw,80px)', background: 'var(--color-background)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(40px,6vw,64px)' }}>
            
            <h2 style={{
              fontSize: 'var(--font-size-page-title)', fontWeight: 700,
              color: 'var(--gray-12)', margin: '0 auto',
              lineHeight: 1.2, letterSpacing: '-0.012em', maxWidth: 480,
            }}>
              Our values in everything we do
            </h2>
          </div>

          <div className="au-values-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 'clamp(16px,3vw,24px)',
          }}>
            {VALUES.map(({ Art, title, body }) => (
              <article key={title} className="au-value-card">
                <div className="au-art"><Art /></div>
                <div className="au-scrim" />
                <div className="au-content">
                  <h3>{title}</h3>
                  <p>{body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   AboutUs page
   ─────────────────────────────────────────────────────────────────────────── */
export default function AboutUs() {
  const pageRef  = useRef(null);
  const statsRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {

      /* ── Hero ── */
      gsap.timeline({ delay: 0.1 })
        .from('.au-hero-badge',   { y: 20, opacity: 0, duration: 0.6, ease: 'power2.out' })
        .from('.au-hero-line',    { y: 52, opacity: 0, duration: 0.9, stagger: 0.18, ease: 'power3.out' }, '-=0.3')
        .from('.au-hero-body',    { y: 24, opacity: 0, duration: 0.7, ease: 'power2.out' }, '-=0.5')
        .from('.au-hero-tags',    { opacity: 0, duration: 0.6 }, '-=0.4')
        .from('.au-scroll-hint',  { opacity: 0, duration: 0.8 }, '-=0.3');

      /* ── Split sections: image slides in from side, text stagger-reveals ── */
      gsap.utils.toArray('.split-section').forEach((sec, i) => {
        const imgDir = i % 2 === 0 ? -1 : 1;
        const img    = sec.querySelector('.img-side');
        const items  = sec.querySelectorAll('.au-item');

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: sec,
            start: 'top 72%',
            toggleActions: 'play none none reverse',
          },
        });

        if (img) {
          tl.from(img, { x: imgDir * 80, opacity: 0, duration: 1.1, ease: 'power3.out' });
        }
        tl.from(items, {
          y: 36, opacity: 0, duration: 0.75, stagger: 0.13, ease: 'power2.out',
        }, img ? '-=0.75' : 0);
      });

      /* ── Stat counters ── */
      if (statsRef.current) {
        statsRef.current.querySelectorAll('[data-count]').forEach(el => {
          const target = parseFloat(el.dataset.count);
          const float  = el.dataset.float === 'true';
          const obj    = { val: 0 };
          gsap.to(obj, {
            val: target, duration: 2.2, ease: 'power2.out',
            scrollTrigger: { trigger: statsRef.current, start: 'top 80%', once: true },
            onUpdate: () => {
              el.textContent = float
                ? obj.val.toFixed(1)
                : Math.round(obj.val).toLocaleString();
            },
          });
        });
      }

      /* ── Value cards stagger ── */
      gsap.from('.au-value-card', {
        y: 56, opacity: 0, duration: 0.8, stagger: 0.14, ease: 'power3.out',
        scrollTrigger: {
          trigger: '.au-values-grid',
          start: 'top 74%',
          toggleActions: 'play none none reverse',
        },
      });

    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <div
      ref={pageRef}
      style={{ background: 'var(--color-background)', fontFamily: 'Poppins, sans-serif', overflowX: 'hidden' }}
    >

      {/* ══ 1. HERO ══════════════════════════════════════════════════════════════ */}
      <section
        style={{
          padding: 'clamp(72px, 10vw, 120px) clamp(24px, 6vw, 80px) clamp(80px, 10vw, 120px)',
          background: 'var(--color-background)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{
          maxWidth: 1100, margin: '0 auto',
          display: 'flex', flexWrap: 'wrap',
          alignItems: 'center',
          gap: 'clamp(32px, 5vw, 64px)',
        }}>

          {/* Left: animation */}
          <div
            className="au-hero-badge"
            style={{ flex: '1 1 340px', minWidth: 0 }}
          >
            <DotLottieReact
              src="https://lottie.host/66983ed5-9bdb-48ba-87e9-27caacde5cdb/8LqTPXtI46.lottie"
              loop
              autoplay
              style={{ width: '100%' }}
            />
          </div>

          {/* Right: text */}
          <div style={{ flex: '1 1 340px', minWidth: 0 }}>

            <h1 style={{ margin: '0 0 20px', lineHeight: 1.1 }}>
              <span
                className="au-hero-line"
                style={{
                  display: 'block',
                  fontSize: 'var(--font-size-hero)',
                  fontWeight: 800, color: 'var(--gray-12)',
                  letterSpacing: '-0.02em',
                }}
              >
                Where Beauty
              </span>
              <span
                className="au-hero-line"
                style={{
                  display: 'block',
                  fontSize: 'var(--font-size-hero)',
                  fontWeight: 800, color: 'var(--orange-9)',
                  letterSpacing: '-0.02em',
                }}
              >
                Meets Excellence
              </span>
            </h1>

            <p
              className="au-hero-body"
              style={{
                color: 'var(--gray-11)',
                fontSize: 'var(--font-size-lead)',
                lineHeight: 1.7, marginBottom: 28,
              }}
            >
              We're building East Africa's most trusted beauty marketplace — connecting
              clients with world-class providers through transparency, technology, and
              genuine care.
            </p>

          </div>
        </div>
      </section>

      {/* ══ 2. VISION ════════════════════════════════════════════════════════════ */}
      <SplitSection
        eyebrow="Our Vision"
        title="A world where every person feels their best"
        body="We envision a continent where world-class beauty services are accessible to everyone — where finding a skilled provider in your city takes seconds, not days, and where every appointment leaves you feeling seen, cared for, and confident."
        cta="Learn about our impact"
        dotLottieSrc="https://lottie.host/4fe9e8c2-850a-4eb6-803a-339259bfe21c/5lWRJwAEto.lottie"
        lottieUrl={LOTTIE_URLS.vision}
        FallbackIcon={EyeIcon}
        imgOnRight
      />

      {/* ══ 3. MISSION ═══════════════════════════════════════════════════════════ */}
      <SplitSection
        eyebrow="Our Mission"
        title="Empowering providers, delighting clients"
        body="We remove the friction between great beauty services and the people who need them. By equipping independent providers with online presence, scheduling, payments, and analytics, we help them grow while giving clients a seamless, trustworthy experience every time."
        cta="Join as a provider"
        dotLottieSrc="https://lottie.host/2fc37883-90e7-4266-b5a4-817a5b1cdf73/c3yJHUJW4S.lottie"
        lottieUrl={LOTTIE_URLS.mission}
        customIcon={
          <FontAwesomeIcon
            icon={faBullseye}
            style={{ width: 40, height: 40, color: 'var(--orange-9)' }}
          />
        }
        tinted
      />

      {/* ══ 4. STORY ═════════════════════════════════════════════════════════════ */}
      <SplitSection
        eyebrow="Our Story"
        title="Built from a real problem, by people who care"
        body="Salon started when our founders spent weeks trying to find a reliable hairstylist in Kampala — only to be let down by inconsistent service and unclear pricing. That frustration became a mission: build the platform we wished existed. Today thousands of clients and providers across East Africa trust Salon to make beauty services better for everyone."
        cta="Read the full story"
        dotLottieSrc="https://lottie.host/ee630924-d45a-4c71-a97c-9a40e7798943/LaEqQJLzg9.lottie"
        lottieUrl={LOTTIE_URLS.story}
        FallbackIcon={BookOpenIcon}
        imgOnRight
      />

      {/* ══ 5. VALUES ════════════════════════════════════════════════════════════ */}
      <ValuesSection />

    </div>
  );
}
